import * as api from "@modules/api";
import { handleDispatchResult } from "@utils/helpers/handleDispatchResult";

import { store } from "../store";
import { AuthActions } from "./slices";
import { GetMyInfoInput, SignInInput, SignUpInput } from "./types";
import { clearContentState } from "../content";
import { clearMyLibraryState } from "../myLibrary";
import { clearReaderStatisticsState } from "../readerStatistics";
import { clearStorage } from "@modules/storage/storageHelper";

export const signIn = async (
  body: SignInInput,
  onSuccess?: () => void | Promise<void>
) => {
  const res = await store.dispatch(AuthActions.signInAsync(body));

  return handleDispatchResult(res, onSuccess);
};

export const signInWithGoogle = async (
  body: api.Server.GoogleLoginV2Input,
  onSuccess?: () => void | Promise<void>
) => {
  const res = await store.dispatch(AuthActions.signInWithGoogleAsync(body));

  return handleDispatchResult(res, onSuccess);
};

export const requestPhoneNumberOtp = async (
  body: api.Server.RequestWhatsAppOTPInput,
  onSuccess?: () => void | Promise<void>
) => {
  const res = await store.dispatch(
    AuthActions.requestPhoneNumberOtpAsync(body)
  );

  return handleDispatchResult(res, onSuccess);
};

export const signInWithPhoneNumber = async (
  body: api.Server.WhatsAppLoginInput,
  onSuccess?: () => void | Promise<void>
) => {
  const res = await store.dispatch(
    AuthActions.signInWithPhoneNumberAsync(body)
  );

  return handleDispatchResult(res, onSuccess);
};

export const signInWithB2B = async (
  body: api.Server.AuthenticateB2BInput,
  onSuccess?: () => void | Promise<void>
) => {
  const res = await store.dispatch(AuthActions.signInWithB2BAsync(body));

  return handleDispatchResult(res, onSuccess);
};

export const signInWithB2BByToken = async (
  body: api.Server.B2BLoginInput,
  onSuccess?: (response: api.Server.AuthenticateOutput) => void | Promise<void>
) => {
  const res = await store.dispatch(AuthActions.signInWithB2BByTokenAsync(body));

  return handleDispatchResult(res, onSuccess);
};

export const signOut = async () => {
  await store.dispatch(AuthActions.logoutAsync());

  clearContentState();
  clearMyLibraryState();
  clearReaderStatisticsState();

  return;
};

export const signUp = async (
  body: SignUpInput,
  onSuccess?: () => void | Promise<void>
) => {
  const res = await store.dispatch(AuthActions.signUpAsync(body));

  return handleDispatchResult(res, onSuccess);
};

// export const signInWithGoogle = async (
//   body: SignInWithGoogleInput,
//   onSuccess?: () => void | Promise<void>
// ) => {
//   const res = await store.dispatch(signInWithGoogleAsync(body));

//   return handleDispatchResult(res, onSuccess);
// };

export const checkB2BLoginOption = async (
  body: api.Server.CheckLoginOptionInput,
  onSuccess?: () => void | Promise<void>
) => {
  const res = await store.dispatch(AuthActions.checkB2BLoginOptionAsync(body));

  return handleDispatchResult(res, onSuccess);
};

export const clearCheckB2BLoginOption = async () => {
  store.dispatch(AuthActions.clearCheckB2BLoginOption());
};

export const setUser = async (user: api.Server.AuthenticateOutput) => {
  store.dispatch(AuthActions.setUser(user));
};

export const setTokens = async (user: api.Server.AuthenticateOutput) => {
  store.dispatch(AuthActions.setTokens(user));
};

export const setIsContentPrepareLoading = async (isLoading: boolean) => {
  store.dispatch(AuthActions.setIsContentPrepareLoading(isLoading));
};

export const setOnboardingCompleted = async (
  onSuccess?: () => void | Promise<void>
) => {
  const res = await store.dispatch(AuthActions.setOnboardingCompletedAsync());

  return handleDispatchResult(res, onSuccess);
};

export const changePassword = async (
  body: api.Server.ChangeMyPasswordInput,
  token: string,
  onSuccess?: () => void | Promise<void>
) => {
  const res = await store.dispatch(
    AuthActions.changePasswordAsync({
      data: body,
      token,
    })
  );

  return handleDispatchResult(res, onSuccess);
};

export const requestPasswordReset = async (
  body: api.Server.RequestPasswordResetThroughUserEmailInput,
  onSuccess?: () => void | Promise<void>
) => {
  const res = await store.dispatch(AuthActions.requestPasswordResetAsync(body));

  return handleDispatchResult(res, onSuccess);
};

export const verifyPasswordReset = async (
  body: api.Server.VerifyPasswordResetCodeThroughUserEmailInput,
  onSuccess?: () => void | Promise<void>
) => {
  const res = await store.dispatch(
    AuthActions.verifyPasswordResetRequestAsync(body)
  );

  return handleDispatchResult(res, onSuccess);
};

export const resetPassword = async (
  body: api.Server.ChangePasswordWithResetCodeThroughUserEmailInput,
  onSuccess?: () => void | Promise<void>
) => {
  const res = await store.dispatch(AuthActions.resetPasswordAsync(body));

  return handleDispatchResult(res, onSuccess);
};

export const checkEmail = async (
  email: string,
  onSuccess?: (payload: boolean) => void | Promise<void>
) => {
  const res = await store.dispatch(AuthActions.checkEmailAsync(email));

  return handleDispatchResult(res, onSuccess);
};

export const clearEmailExist = async () => {
  store.dispatch(AuthActions.clearEmailExist());
};

export const registerToBusinessGroup = async (
  body: api.Server.AttachBusinessToRegisteredUserInput,
  onSuccess?: () => void | Promise<void>
) => {
  const res = await store.dispatch(
    AuthActions.registerToBusinessGroupAsync(body)
  );

  return handleDispatchResult(res, onSuccess);
};

export const setPasswordByInvitationToken = async (
  body: api.Server.SetUserPasswordByTokenInput,
  onSuccess?: () => void | Promise<void>
) => {
  const res = await store.dispatch(
    AuthActions.setPasswordByInvitationTokenAsync(body)
  );

  return handleDispatchResult(res, onSuccess);
};

export const updateUsername = async (
  body: api.Server.UpdateCurrentUserNameInput,
  onSuccess?: () => void | Promise<void>
) => {
  const res = await store.dispatch(AuthActions.updateUsernameAsync(body));

  return handleDispatchResult(res, onSuccess);
};

export const changeEmail = async (
  input: api.Server.ChangeEmailInput,
  onSuccess?: (payload: boolean) => void | Promise<void>
) => {
  const res = await store.dispatch(AuthActions.changeEmailAsync(input));

  return handleDispatchResult(res, onSuccess);
};

export const requestDeleteAccount = async (
  onSuccess?: (payload: boolean) => void | Promise<void>
) => {
  const res = await store.dispatch(AuthActions.requestDeleteAccountrAsync());

  return handleDispatchResult(res, onSuccess);
};

export const setMyEmailPassword = async (
  input: api.Server.SetMyEmailAndPasswordInput,
  onSuccess?: (payload: boolean) => void | Promise<void>
) => {
  const res = await store.dispatch(AuthActions.setMyEmailPasswordAsync(input));

  return handleDispatchResult(res, onSuccess);
};

export const changMyPassword = async (
  input: api.Server.ChangeMyPasswordInput,
  onSuccess?: (payload: boolean) => void | Promise<void>
) => {
  const res = await store.dispatch(AuthActions.changMyPasswordAsync(input));

  return handleDispatchResult(res, onSuccess);
};

export const checkPhoneNumber = async (
  input: string,
  onSuccess?: (payload: boolean) => void | Promise<void>
) => {
  const res = await store.dispatch(AuthActions.checkPhoneNumberAsync(input));

  return handleDispatchResult(res, onSuccess);
};

export const linkGoogle = async (
  input: api.Server.LinkGoogleLoginInput,
  onSuccess?: (payload: boolean) => void | Promise<void>
) => {
  const res = await store.dispatch(AuthActions.linkGoogleAsync(input));

  return handleDispatchResult(res, onSuccess);
};

export const linkApple = async (
  input: api.Server.LinkAppleLoginInput,
  onSuccess?: (payload: boolean) => void | Promise<void>
) => {
  const res = await store.dispatch(AuthActions.linkAppleAsync(input));

  return handleDispatchResult(res, onSuccess);
};

export const refreshToken = async (
  input: api.Server.RefreshTokenV2Input,
  onSuccess?: () => void | Promise<void>,
  onFailure?: () => void | Promise<void>
) => {
  const res = await store.dispatch(AuthActions.refreshTokenAsync(input));

  return handleDispatchResult(res, onSuccess, onFailure);
};

export const getMyInfo = async (
  input: GetMyInfoInput,
  onSuccess?: () => void | Promise<void>,
  onFailure?: () => void | Promise<void>
) => {
  const res = await store.dispatch(AuthActions.getMyInfoAsync(input));

  return handleDispatchResult(res, onSuccess, onFailure);
};
